import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { modifier } from 'ember-modifier';
import PerfectScrollbar from 'perfect-scrollbar';

type PerfectScrollbarOptions = {
  customXrailOffset?: number;
  customYrailOffset?: number;
  suppressScrollX?: boolean;
  suppressScrollY?: boolean;
  wheelPropagation?: boolean;
};

interface InitializeSignature {
  Args: {
    Named: {
      callback: (perfectScrollbar: PerfectScrollbar) => void;
      containerId?: string;
      options: Required<PerfectScrollbarOptions>;
    };
    Positional: [];
  };
  Element: HTMLElement;
}

const initialize = modifier<InitializeSignature>(
  (element, _positional, named) => {
    const { callback, containerId, options } = named;
    let perfectScrollbarElement = element;

    if (containerId) {
      perfectScrollbarElement = document.getElementById(containerId)!;
    }

    const perfectScrollbar = new PerfectScrollbar(
      perfectScrollbarElement,
      options,
    );

    callback(perfectScrollbar);

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => {
      perfectScrollbar.destroy();
    };
  },
);

interface PrismPerfectScrollbarSignature {
  Args: PerfectScrollbarOptions & {
    customContainerId?: string;
  };
  Blocks: {
    default: [PerfectScrollbar];
  };
  Element: HTMLDivElement;
}

export default class PrismPerfectScrollbar extends Component<PrismPerfectScrollbarSignature> {
  @tracked declare perfectScrollbar: PerfectScrollbar;

  initialize = initialize;

  get options(): Required<PerfectScrollbarOptions> {
    const {
      customXrailOffset,
      customYrailOffset,
      suppressScrollX,
      suppressScrollY,
      wheelPropagation,
    } = this.args;

    return {
      customXrailOffset: customXrailOffset ?? 0,
      customYrailOffset: customYrailOffset ?? 0,
      suppressScrollX: suppressScrollX ?? false,
      suppressScrollY: suppressScrollY ?? false,
      wheelPropagation: wheelPropagation ?? true,
    };
  }

  @action updatePerfectScrollbar(perfectScrollbar: PerfectScrollbar): void {
    this.perfectScrollbar = perfectScrollbar;
  }

  <template>
    <div ...attributes>
      <div
        class="perfect-scrollbar-container"
        {{this.initialize
          callback=this.updatePerfectScrollbar
          containerId=@customContainerId
          options=this.options
        }}
      >
        {{yield this.perfectScrollbar}}
      </div>
    </div>
  </template>
}
